// Ported from cartadigital-pos app/utils/order-grouping.ts (icons mapped to mdi).
import { OrderStatus, OrderType } from '@/enums/OrderEnums';
import type { IOrderListItem } from './pos-types';

export interface IOrderGroup {
  status: OrderStatus | 'needs-attention';
  label: string;
  /** Admin extra: posV2.* key so components can render translated copy. */
  i18nKey: string;
  icon: string;
  color: string;
  orders: IOrderListItem[];
  priority: number;
}

const STATUS_CONFIG: Record<
  OrderStatus,
  {
    label: string;
    i18nKey: string;
    icon: string;
    color: string;
    priority: number;
  }
> = {
  [OrderStatus.Received]: {
    label: 'Nuevos',
    i18nKey: 'posV2.groupNew',
    icon: 'bell',
    color: 'purple',
    priority: 5,
  },
  [OrderStatus.Progress]: {
    label: 'En Progreso',
    i18nKey: 'posV2.groupProgress',
    icon: 'chef-hat',
    color: 'yellow',
    priority: 3,
  },
  [OrderStatus.ReadyToTake]: {
    label: 'Listos para Entregar',
    i18nKey: 'posV2.groupReady',
    icon: 'check-circle',
    color: 'green',
    priority: 1,
  },
  [OrderStatus.Delivering]: {
    label: 'En Camino',
    i18nKey: 'posV2.groupDelivering',
    icon: 'truck-delivery',
    color: 'blue',
    priority: 2,
  },
  [OrderStatus.Finished]: {
    label: 'Finalizados',
    i18nKey: 'posV2.groupFinished',
    icon: 'check-circle',
    color: 'gray',
    priority: 6,
  },
  [OrderStatus.Declined]: {
    label: 'Rechazados',
    i18nKey: 'posV2.groupDeclined',
    icon: 'close-circle',
    color: 'red',
    priority: 7,
  },
  [OrderStatus.Canceled]: {
    label: 'Cancelados',
    i18nKey: 'posV2.groupCanceled',
    icon: 'cancel',
    color: 'gray',
    priority: 8,
  },
};

/**
 * Groups orders by their status and returns an array of groups sorted by priority
 * @param orders Array of orders to group
 * @param orderType Type of orders (to filter out inapplicable groups like Delivering for Away orders)
 * @returns Array of order groups sorted by priority
 */
export function groupOrdersByStatus(
  orders: IOrderListItem[],
  orderType: OrderType
): IOrderGroup[] {
  // First, check if there are any orders that need attention
  const needsAttentionOrders = orders.filter((o) => o.needsAttention);

  // Group orders by status
  const groupedMap = new Map<OrderStatus, IOrderListItem[]>();

  orders.forEach((order) => {
    // Skip orders already in needs-attention group
    if (order.needsAttention) return;

    const status = order.status as OrderStatus;
    if (!groupedMap.has(status)) {
      groupedMap.set(status, []);
    }
    groupedMap.get(status)!.push(order);
  });

  // Convert to array of groups
  const groups: IOrderGroup[] = [];

  // Add needs-attention group first if there are any
  if (needsAttentionOrders.length > 0) {
    groups.push({
      status: 'needs-attention',
      label: '⚠️ Requiere Atención',
      i18nKey: 'posV2.groupAttention',
      icon: 'alert-circle',
      color: 'red',
      orders: needsAttentionOrders,
      priority: 0, // Highest priority
    });
  }

  // Add other status groups
  groupedMap.forEach((groupOrders, status) => {
    // Skip Delivering group for Away orders
    if (orderType === OrderType.Away && status === OrderStatus.Delivering) {
      return;
    }

    // Skip Received status (nuevos - they go in incoming pane)
    if (status === OrderStatus.Received) {
      return;
    }

    const config = STATUS_CONFIG[status];
    if (config && groupOrders.length > 0) {
      groups.push({
        status,
        label: config.label,
        i18nKey: config.i18nKey,
        icon: config.icon,
        color: config.color,
        orders: groupOrders,
        priority: config.priority,
      });
    }
  });

  // Sort groups by priority
  return groups.sort((a, b) => a.priority - b.priority);
}
